import React, { useEffect, useMemo, useState } from "react";
import { ShoppingCart, Users, Sparkles } from "lucide-react";
import { OrderItem } from "../types";
import { listenCustomerDisplay } from "../lib/customerDisplay";

interface CustomerDisplayProps {
  cart?: OrderItem[];
  totalAmount?: number;
  customerName?: string;
  tableNumber?: string;
  orderType?: string;
}

type CustomerStatePayload = {
  cart: OrderItem[];
  totalAmount: number;
  customerName?: string;
  tableNumber?: string;
  orderType?: string;
};

function prettyOrderType(v?: string) {
  if (!v) return "";
  return v.replace(/_/g, " ");
}

const CustomerDisplay: React.FC<CustomerDisplayProps> = (props) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  const [state, setState] = useState<CustomerStatePayload>({
    cart: props.cart ?? [],
    totalAmount: props.totalAmount ?? 0,
    customerName: props.customerName,
    tableNumber: props.tableNumber,
    orderType: props.orderType,
  });

  const cart = props.cart ?? state.cart;
  const totalAmount = props.totalAmount ?? state.totalAmount;
  const customerName = props.customerName ?? state.customerName;
  const tableNumber = props.tableNumber ?? state.tableNumber;
  const orderType = props.orderType ?? state.orderType;

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // ✅ Native Presentation WebView -> injected JS calls this function
  useEffect(() => {
    (window as any).__FORKCAST_RECEIVE = (payload: any) => {
      if (!payload || typeof payload !== "object") return;

      // payload is the object you pass from Till: {cart,totalAmount,customerName,tableNumber,orderType}
      const nextCart = Array.isArray(payload.cart) ? (payload.cart as OrderItem[]) : [];
      const nextTotal = Number(payload.totalAmount ?? 0);

      setState({
        cart: nextCart,
        totalAmount: Number.isFinite(nextTotal) ? nextTotal : 0,
        customerName: typeof payload.customerName === "string" ? payload.customerName : undefined,
        tableNumber: typeof payload.tableNumber === "string" ? payload.tableNumber : undefined,
        orderType: typeof payload.orderType === "string" ? payload.orderType : undefined,
      });
    };

    return () => {
      try {
        delete (window as any).__FORKCAST_RECEIVE;
      } catch {}
    };
  }, []);

  // ✅ Web popup / normal browser syncing
  useEffect(() => {
    const unsub = listenCustomerDisplay((msg: any) => {
      if (!msg || typeof msg !== "object") return;

      if (msg.type === "CLEAR_ORDER") {
        setState({ cart: [], totalAmount: 0 });
        return;
      }

      if (msg.type === "UPDATE_ORDER") {
        const p = msg.payload ?? {};
        const nextCart = Array.isArray(p.cart) ? (p.cart as OrderItem[]) : [];
        const nextTotal = Number(p.totalAmount ?? 0);

        setState({
          cart: nextCart,
          totalAmount: Number.isFinite(nextTotal) ? nextTotal : 0,
          customerName: typeof p.customerName === "string" ? p.customerName : undefined,
          tableNumber: typeof p.tableNumber === "string" ? p.tableNumber : undefined,
          orderType: typeof p.orderType === "string" ? p.orderType : undefined,
        });
      }
    });

    return unsub;
  }, []);

  const hasMeta = Boolean(tableNumber || customerName || orderType);
  const hasItems = cart.length > 0;

  const lines = useMemo(() => {
    return cart.map((item) => {
      const base = Number(item.menu_item?.price ?? 0);
      // @ts-ignore
      const mealAdd = item.is_meal ? Number(item.menu_item?.meal_price_add ?? 0) : 0;
      const unit = base + mealAdd;
      const qty = Number(item.quantity ?? 0);

      return {
        id: item.id,
        name: item.menu_item?.name ?? "Item",
        qty,
        total: unit * qty,
        isMeal: Boolean((item as any).is_meal),
        side: (item as any).meal_side as string | undefined,
        drink: (item as any).meal_drink as string | undefined,
        notes: (item as any).notes as string | undefined,
      };
    });
  }, [cart]);

  return (
    <div className="h-screen bg-slate-50 text-slate-900 flex flex-col overflow-hidden">
      <div className="bg-white border-b border-slate-200 flex-shrink-0">
        <div className="px-8 py-6 flex items-center justify-between gap-6">
          <div className="min-w-0">
            <div className="text-sm text-slate-500">Customer Display</div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 leading-tight">Forkcast</h1>
            <div className="mt-1 text-sm text-slate-500">Live order preview • Prices update as items are added</div>
          </div>

          <div className="text-right flex-shrink-0">
            <div className="text-2xl font-extrabold tabular-nums text-slate-900">
              {currentTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </div>
            <div className="text-sm text-slate-500">{currentTime.toLocaleDateString()}</div>
          </div>
        </div>

        {hasMeta && (
          <div className="px-8 pb-6">
            <div className="rounded-2xl bg-slate-900 text-white shadow-sm overflow-hidden">
              <div className="px-6 py-4 flex flex-wrap items-center justify-center gap-6">
                {tableNumber && (
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-2xl bg-white/10 flex items-center justify-center">
                      <Users size={20} className="text-white/90" />
                    </div>
                    <div className="text-left">
                      <div className="text-lg font-extrabold">Table {tableNumber}</div>
                      <div className="text-xs text-white/70">Your table</div>
                    </div>
                  </div>
                )}

                {customerName && (
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-2xl bg-white/10 flex items-center justify-center">
                      <Users size={20} className="text-white/90" />
                    </div>
                    <div className="text-left">
                      <div className="text-lg font-extrabold truncate max-w-[240px]">{customerName}</div>
                      <div className="text-xs text-white/70">Customer</div>
                    </div>
                  </div>
                )}

                {orderType && (
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-2xl bg-white/10 flex items-center justify-center">
                      <ShoppingCart size={20} className="text-white/90" />
                    </div>
                    <div className="text-left">
                      <div className="text-lg font-extrabold capitalize">{prettyOrderType(orderType)}</div>
                      <div className="text-xs text-white/70">Order type</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 px-8 py-6">
        {!hasItems ? (
          <div className="h-full rounded-3xl bg-white ring-1 ring-slate-200 shadow-sm flex flex-col items-center justify-center text-center p-10">
            <div className="h-14 w-14 rounded-2xl bg-slate-900 text-white flex items-center justify-center">
              <ShoppingCart size={28} />
            </div>
            <h2 className="mt-5 text-2xl font-extrabold text-slate-900">Your order will appear here</h2>
            <p className="mt-2 text-slate-500 max-w-xl">As items are added on the till, you’ll see them instantly on this screen.</p>

            <div className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-slate-50 ring-1 ring-slate-200 px-4 py-2 text-sm font-semibold text-slate-700">
              <Sparkles size={16} className="text-slate-500" />
              Waiting for items…
            </div>
          </div>
        ) : (
          <div className="h-full grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 min-h-0">
            <div className="min-h-0 rounded-3xl bg-white ring-1 ring-slate-200 shadow-sm overflow-hidden flex flex-col">
              <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-2xl bg-slate-900 text-white flex items-center justify-center">
                    <ShoppingCart size={20} />
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">Items</div>
                    <div className="text-xl font-extrabold text-slate-900">Your Order</div>
                  </div>
                </div>

                <div className="text-sm font-semibold text-slate-600">{lines.reduce((sum, l) => sum + l.qty, 0)} item(s)</div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-3">
                {lines.map((l) => (
                  <div key={l.id} className="rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-4 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-lg font-extrabold text-slate-900 truncate">{l.name}</div>

                      {l.isMeal && (l.side || l.drink) && (
                        <div className="mt-1 text-sm text-slate-600">
                          {l.side ? (
                            <span className="mr-3">
                              Side: <span className="font-semibold">{l.side}</span>
                            </span>
                          ) : null}
                          {l.drink ? (
                            <span>
                              Drink: <span className="font-semibold">{l.drink}</span>
                            </span>
                          ) : null}
                        </div>
                      )}

                      {l.notes && (
                        <div className="mt-2 text-sm font-semibold text-amber-700 bg-amber-50 ring-1 ring-amber-200 inline-block rounded-xl px-3 py-1">
                          Note: {l.notes}
                        </div>
                      )}
                    </div>

                    <div className="flex-shrink-0 text-right">
                      <div className="text-sm font-extrabold text-slate-700">{l.qty}x</div>
                      <div className="text-xl font-extrabold tabular-nums text-emerald-700">£{l.total.toFixed(2)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl bg-white ring-1 ring-slate-200 shadow-sm overflow-hidden flex flex-col">
              <div className="px-6 py-5 border-b border-slate-200">
                <div className="text-sm text-slate-500">Summary</div>
                <div className="text-xl font-extrabold text-slate-900">Total</div>
              </div>

              <div className="px-6 py-6 flex-1 flex flex-col justify-between">
                <div className="rounded-2xl bg-slate-900 text-white p-6">
                  <div className="text-sm text-white/70">Amount due</div>
                  <div className="mt-2 text-4xl font-extrabold tabular-nums">£{Number(totalAmount ?? 0).toFixed(2)}</div>
                </div>

                <div className="mt-6 rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-4 text-sm text-slate-600 font-semibold">
                  Please check your items and total before paying.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border-t border-slate-200 px-8 py-4 text-center flex-shrink-0">
        <p className="text-slate-500 font-semibold">Thank you for choosing Forkcast • We appreciate your business</p>
      </div>
    </div>
  );
};

export default CustomerDisplay;
